import { useEffect, useState } from "react";
import {
  EuiBadge,
  EuiButtonEmpty,
  EuiCallOut,
  EuiFlexGrid,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHealth,
  EuiIcon,
  EuiLink,
  EuiLoadingSpinner,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTitle,
  EuiToolTip,
} from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";
import {
  ApplianceFreshness,
  WaveSummary,
  formatTimeAgo,
  waveStateColor,
} from "./model";
import { useWaveStateLabel } from "./rail";
import { useWaveAppliances } from "./useWaveAppliances";

/** One-line explanation of what the wave's state means for the operator. */
const useStateDetail = (): ((summary: WaveSummary) => string) => {
  const { translate } = useAnketaI18n();
  return (summary: WaveSummary) => {
    switch (summary.state) {
      case "root":
        return translate("anketa.opsanio.waves.hint.root", {
          defaultMessage:
            "dev tracks floating tags. Versions are pinned from main onwards.",
        });
      case "unconfig":
        return translate("anketa.opsanio.waves.hint.unconfigured", {
          defaultMessage:
            "No version set has been pinned for this wave yet.",
        });
      case "blocked":
        return translate("anketa.opsanio.waves.hint.blocked", {
          defaultMessage:
            "The parent wave must be up to date and free of in-flight updates.",
        });
      case "inflight":
        return translate("anketa.opsanio.waves.hint.inflight", {
          defaultMessage:
            "Merging rolls the pinned versions out to every appliance on this wave.",
        });
      case "updates":
        return translate("anketa.opsanio.waves.hint.updates", {
          defaultMessage:
            "Creating an update opens a merge request on the FluxCD config repo.",
        });
      default:
        return translate("anketa.opsanio.waves.hint.current", {
          defaultMessage: "Nothing to promote - this wave matches its parent.",
        });
    }
  };
};

/**
 * Header card for the selected wave: identity, one state pill, the key pinned
 * metadata, the appliance roster behind a summary chip, and the blocked-promotion
 * callout when the parent gate is closed.
 */
export const WaveDetailHeader = ({
  summary,
}: {
  summary: WaveSummary;
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  const stateLabel = useWaveStateLabel();
  const stateDetail = useStateDetail();

  const [appliancesOpen, setAppliancesOpen] = useState<boolean>(false);
  // Fetched on first expand and kept while the wave stays selected; the
  // summary chip already answers "how many" without it.
  const [requested, setRequested] = useState<boolean>(false);

  // Collapse the roster when the operator switches waves, so an expanded list
  // never carries the previous wave's appliances.
  useEffect(() => {
    setAppliancesOpen(false);
    setRequested(false);
  }, [summary.wave]);

  // Roster first (deployment + PRTG), fork states filled in progressively.
  const {
    appliances: detail,
    loading: isLoadingDetail,
    forksPending,
  } = useWaveAppliances(summary.wave, requested);

  const toggleAppliances = (): void => {
    if (!appliancesOpen) setRequested(true);
    setAppliancesOpen(!appliancesOpen);
  };

  const forkStale = (a: ApplianceFreshness): boolean =>
    a.forksChecked !== false && !a.allFresh;

  const applianceIssues = (detail ?? []).filter(
    (a) => a.deployed && (forkStale(a) || (a.prtg.configured && a.prtg.missing)),
  ).length;

  const applianceRow = (a: ApplianceFreshness): JSX.Element => {
    const problem =
      forkStale(a) || (a.prtg.configured && a.prtg.missing)
        ? forkStale(a)
          ? translate("anketa.opsanio.waves.appliance.stale_fork", {
              defaultMessage: "stale fork",
            })
          : translate("anketa.opsanio.waves.appliance.prtg_missing", {
              defaultMessage: "PRTG missing",
            })
        : "";
    const color = !a.deployed ? "subdued" : problem ? "warning" : "success";

    return (
      <EuiFlexItem key={a.vpc}>
        <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
          <EuiFlexItem style={{ minWidth: 0 }}>
            <EuiHealth color={color} textSize="xs" className="eui-textTruncate">
              {a.vpc}
            </EuiHealth>
          </EuiFlexItem>
          {!a.deployed && (
            <EuiFlexItem grow={false}>
              <EuiText size="xs" color="subdued">
                {translate("anketa.opsanio.waves.appliance.not_deployed", {
                  defaultMessage: "not deployed",
                })}
              </EuiText>
            </EuiFlexItem>
          )}
          {problem && (
            <EuiFlexItem grow={false}>
              <EuiText size="xs" color="subdued">
                {problem}
              </EuiText>
            </EuiFlexItem>
          )}
          {a.forksChecked === false && (
            <EuiFlexItem grow={false}>
              <EuiLoadingSpinner size="s" />
            </EuiFlexItem>
          )}
          {a.prtg.link && (
            <EuiFlexItem grow={false}>
              <EuiLink href={a.prtg.link} target="_blank">
                <EuiIcon type="visGauge" size="s" />
              </EuiLink>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </EuiFlexItem>
    );
  };

  return (
    <EuiPanel hasBorder paddingSize="l" hasShadow={false}>
      <EuiFlexGroup alignItems="flexStart" gutterSize="m" wrap>
        <EuiFlexItem style={{ minWidth: 0 }}>
          <EuiFlexGroup gutterSize="s" alignItems="center" wrap responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiText size="xs" color="subdued">
                <strong>
                  {translate("anketa.opsanio.waves.stage_label", {
                    values: { n: summary.stage },
                    defaultMessage: "STAGE {{n}}",
                  })}
                </strong>
              </EuiText>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiTitle size="s">
                <h2 style={{ textTransform: "uppercase" }}>{summary.wave}</h2>
              </EuiTitle>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiHealth color={waveStateColor(summary.state)} textSize="s">
                {stateLabel(summary)}
              </EuiHealth>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiSpacer size="xs" />
          <EuiText size="s" color="subdued">
            {stateDetail(summary)}
          </EuiText>
        </EuiFlexItem>

        <EuiFlexItem grow={false}>
          <EuiText size="xs" color="subdued" textAlign="right">
            <div>
              {summary.parentWave
                ? translate("anketa.opsanio.waves.promotes_from", {
                    values: { parent: summary.parentWave },
                    defaultMessage: "promotes from {{parent}}",
                  })
                : translate("anketa.opsanio.waves.no_parent", {
                    defaultMessage: "Root wave - no parent",
                  })}
            </div>
            <div>
              {translate("anketa.opsanio.waves.last_updated", {
                values: {
                  time: formatTimeAgo(summary.version?.updatedAt, translate),
                },
                defaultMessage: "Last updated: {{time}}",
              })}
            </div>
          </EuiText>
        </EuiFlexItem>
      </EuiFlexGroup>

      {summary.version && (
        <>
          <EuiSpacer size="m" />
          <EuiFlexGroup gutterSize="s" alignItems="center" wrap responsive={false}>
            {summary.version.opsanio_plugin_tag && (
              <EuiFlexItem grow={false}>
                <EuiToolTip
                  content={translate(
                    "anketa.opsanio.waves.plugin_version.tooltip",
                    { defaultMessage: "Opsanio Plugin Version" },
                  )}
                >
                  <EuiBadge color="hollow" iconType="package">
                    {summary.version.opsanio_plugin_tag}
                  </EuiBadge>
                </EuiToolTip>
              </EuiFlexItem>
            )}
            {summary.version.pullPolicy && (
              <EuiFlexItem grow={false}>
                <EuiToolTip
                  content={translate("anketa.opsanio.waves.pull_policy.tooltip", {
                    defaultMessage: "Docker Pull Policy",
                  })}
                >
                  <EuiBadge color="hollow" iconType="download">
                    {summary.version.pullPolicy}
                  </EuiBadge>
                </EuiToolTip>
              </EuiFlexItem>
            )}
            <EuiFlexItem grow={false}>
              <EuiBadge color="hollow" iconType="branch">
                {summary.parentWave
                  ? `${summary.parentWave} → ${summary.wave}`
                  : summary.wave}
              </EuiBadge>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButtonEmpty
                size="xs"
                flush="both"
                iconSide="right"
                iconType={appliancesOpen ? "arrowUp" : "arrowDown"}
                isDisabled={summary.appliances.length === 0}
                onClick={toggleAppliances}
              >
                {summary.appliances.length === 0
                  ? translate("anketa.opsanio.waves.appliances.none", {
                      defaultMessage: "No appliances",
                    })
                  : translate("anketa.opsanio.waves.appliances.count", {
                      values: { count: summary.appliances.length },
                      defaultMessage: "{{count}} appliances",
                    })}
              </EuiButtonEmpty>
            </EuiFlexItem>
            {applianceIssues > 0 && (
              <EuiFlexItem grow={false}>
                <EuiBadge color="warning" iconType="alert">
                  {translate("anketa.opsanio.waves.appliances.issues", {
                    values: { count: applianceIssues },
                    defaultMessage: "{{count}} need attention",
                  })}
                </EuiBadge>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        </>
      )}

      {appliancesOpen && (
        <>
          <EuiSpacer size="s" />
          <EuiPanel color="subdued" paddingSize="s" hasShadow={false}>
            {(isLoadingDetail || forksPending > 0) && (
              <>
                <EuiFlexGroup
                  gutterSize="s"
                  alignItems="center"
                  responsive={false}
                >
                  <EuiFlexItem grow={false}>
                    <EuiLoadingSpinner size="s" />
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiText size="xs" color="subdued">
                      {isLoadingDetail
                        ? translate(
                            "anketa.opsanio.waves.form.appliances.checking",
                            {
                              defaultMessage:
                                "Checking fork and monitoring status...",
                            },
                          )
                        : translate(
                            "anketa.opsanio.waves.form.appliances.forks_pending",
                            {
                              values: { count: forksPending },
                              defaultMessage:
                                "Checking forks... ({{count}} left)",
                            },
                          )}
                    </EuiText>
                  </EuiFlexItem>
                </EuiFlexGroup>
                <EuiSpacer size="s" />
              </>
            )}
            {(detail ?? []).length > 0 ? (
              <EuiFlexGrid columns={3} gutterSize="s">
                {(detail ?? []).map(applianceRow)}
              </EuiFlexGrid>
            ) : (
              // The names come straight from the questionnaires (the wave
              // summary), so the roster shows before the status arrives.
              <EuiFlexGrid columns={3} gutterSize="s">
                {summary.appliances.map((vpc) => (
                  <EuiFlexItem key={vpc}>
                    <EuiHealth color="subdued" textSize="xs">
                      {vpc}
                    </EuiHealth>
                  </EuiFlexItem>
                ))}
              </EuiFlexGrid>
            )}
          </EuiPanel>
        </>
      )}

      {summary.state === "blocked" && (
        <>
          <EuiSpacer size="m" />
          <EuiCallOut
            size="s"
            color="danger"
            iconType="lock"
            title={translate("anketa.opsanio.waves.blocked.title", {
              defaultMessage: "Promotion blocked",
            })}
          >
            <EuiText size="s">
              {summary.parentBlockedByOpenUpdate
                ? translate("anketa.opsanio.waves.blocked.parent_in_progress", {
                    values: { parent: summary.parentWave },
                    defaultMessage:
                      'Parent wave "{{parent}}" has an update in progress. Finish updating "{{parent}}" first.',
                  })
                : translate("anketa.opsanio.waves.blocked.parent_stale_short", {
                    values: { parent: summary.parentWave },
                    defaultMessage:
                      'Parent wave "{{parent}}" is not up to date. Update "{{parent}}" first.',
                  })}
            </EuiText>
            {summary.parentBehind.length > 0 && (
              <>
                <EuiSpacer size="s" />
                <EuiText size="xs">
                  <strong>
                    {translate("anketa.opsanio.waves.blocked.behind_fields", {
                      defaultMessage: "Fields behind parent",
                    })}
                  </strong>
                </EuiText>
                <EuiSpacer size="xs" />
                <EuiFlexGroup gutterSize="xs" wrap responsive={false}>
                  {summary.parentBehind.map((field) => (
                    <EuiFlexItem grow={false} key={field}>
                      <EuiBadge color="hollow">{field}</EuiBadge>
                    </EuiFlexItem>
                  ))}
                </EuiFlexGroup>
              </>
            )}
          </EuiCallOut>
        </>
      )}
    </EuiPanel>
  );
};
